/**
 * Top-level navigation ids used by the Mobbin-pattern WorkspaceShell.
 *
 * These do NOT replace the page-level `DashboardPage` enum from
 * `Layout.tsx` — they are a higher-level grouping used by the centered
 * top-nav. Each id resolves to the underlying `DashboardPage` via
 * {@link navIdToDashboardPage}.
 *
 * 'agent' is a special id that navigates to the standalone /agent route
 * (outside DashboardContainer) rather than a DashboardPage.
 */
export type WorkspaceNavId =
  | 'home'
  | 'jobs'
  | 'optimizer'
  | 'activity'
  | 'agent'
  | 'more';

import type { DashboardPage } from '../dashboard/components/Layout';

/** Map a top-nav id to the underlying DashboardPage we route to. */
export function navIdToDashboardPage(id: WorkspaceNavId): DashboardPage {
  switch (id) {
    case 'home':      return 'dashboard';
    case 'jobs':      return 'jobs';
    // 'optimizer' is no longer rendered in any rail (the sidebar Optimizer
    // entry was removed when the single-optimize flow was retired). Kept as
    // a defensive fall-through so any stale persisted nav id still resolves.
    case 'optimizer': return 'agent';
    case 'activity':  return 'history';
    // 'agent' is now a DashboardPage routed through DashboardContainer.
    case 'agent':     return 'agent';
    // 'more' opens a dropdown — not directly routable.
    case 'more':      return 'profile';
  }
}

/** Reverse map: derive the active top-nav id from the current DashboardPage. */
export function dashboardPageToNavId(page: DashboardPage): WorkspaceNavId {
  switch (page) {
    case 'dashboard':              return 'home';
    case 'jobs':                   return 'jobs';
    case 'jobs-admin':             return 'more';
    // Legacy single-optimize page redirects to /agent — the matching tab
    // in the rail is therefore 'agent'. The 'optimizer' rail entry has
    // been removed.
    case 'single-optimize':        return 'agent';
    // Journey-setup happens at the start of an agent session; show the
    // agent tab as active while it's open.
    case 'journey-setup':          return 'agent';
    case 'history':                return 'activity';
    case 'agent':                  return 'agent';
    case 'profile':                return 'more';
    case 'account-settings':       return 'more';
    case 'referrals':              return 'more';
    case 'payment-history':        return 'more';
    case 'support':                return 'more';
    case 'product-feedback':       return 'more';
    case 'credits':                return 'more';
    case 'developer':              return 'more';
    case 'ai-companion':           return 'more';
    case 'my-activities':          return 'more';
    case 'prepare-for-interview':  return 'more';
    case 'calendar-notes':         return 'more';
    case 'skill-assessment':       return 'more';
    case 'blog-cms':               return 'more';
    case 'blog-editor':            return 'more';
    default:                       return 'home';
  }
}

/** Pages on which the top GreetingBlock should render. */
export function shouldShowGreeting(_page: DashboardPage): boolean {
  return false;
}
